// =============================================================================
// MeuDriver — Batch U2: Checklist de Onboarding do Instrutor
// =============================================================================

import { Link } from 'react-router-dom'
import { CheckCircle2, Circle, ChevronRight, PartyPopper } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { OnboardingStatus, OnboardingStepKey } from '@/types/database'

// ---------------------------------------------------------------------------
// Config dos passos
// ---------------------------------------------------------------------------

interface StepConfig {
  key: OnboardingStepKey
  label: string
  description: string
  href: string
}

const STEPS: StepConfig[] = [
  {
    key: 'profile',
    label: 'Complete seu perfil',
    description: 'Biografia (20+ caracteres) e cidade',
    href: '/instrutor/perfil',
  },
  {
    key: 'vehicles',
    label: 'Cadastre seu veículo',
    description: 'Modelo, ano e placa do carro de aula',
    href: '/instrutor/veiculos',
  },
  {
    key: 'availability',
    label: 'Defina sua disponibilidade',
    description: 'Dias e horários em que você atende',
    href: '/instrutor/agenda',
  },
  {
    key: 'pricing',
    label: 'Configure o preço por hora',
    description: 'Quanto você cobra por aula',
    href: '/instrutor/perfil',
  },
  {
    key: 'photo',
    label: 'Adicione sua foto',
    description: 'Uma foto profissional aumenta sua conversão',
    href: '/instrutor/perfil',
  },
]

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

interface OnboardingChecklistProps {
  status: OnboardingStatus
  className?: string
}

export function OnboardingChecklist({ status, className }: OnboardingChecklistProps) {
  if (status.is_complete) {
    return (
      <div
        className={cn(
          'rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-4',
          className,
        )}
        data-testid="onboarding-complete"
      >
        <div className="flex items-center gap-3">
          <PartyPopper className="h-6 w-6 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-900 dark:text-emerald-300">
              Perfil completo! Você está pronto para receber alunos.
            </p>
            <p className="text-sm text-emerald-700 dark:text-emerald-500 mt-0.5">
              Seu perfil está 100% configurado. Novos alunos podem encontrar e agendar aulas com você.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const progressPct = Math.round((status.completed_steps / 5) * 100)

  return (
    <div
      className={cn(
        'rounded-xl border border-brand-200 bg-brand-50/50 dark:bg-brand-950/20 dark:border-brand-800 p-4 space-y-4',
        className,
      )}
      data-testid="onboarding-checklist"
    >
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="font-semibold text-gray-900 dark:text-white text-sm">
            Configure seu perfil ({status.completed_steps}/5)
          </p>
          <span className="text-xs font-medium text-brand-600 dark:text-brand-400">
            {progressPct}%
          </span>
        </div>
        <div className="h-2 bg-brand-100 dark:bg-brand-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Os passos se marcam sozinhos quando você preenche cada item.
        </p>
      </div>

      {/* Lista de passos */}
      <div className="space-y-2">
        {STEPS.map(({ key, label, description, href }) => {
          const done = status[`step_${key}` as `step_${OnboardingStepKey}`]
          return (
            <div
              key={key}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors',
                done
                  ? 'bg-emerald-50/70 dark:bg-emerald-950/20'
                  : 'bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700',
              )}
            >
              {/* Checkbox — clique marca como feito */}
              {/* O status vem dos dados reais (get_onboarding_status); marcar à mão não vale mais. */}
              <span
                data-testid={`onboarding-step-${key}`}
                className="shrink-0"
                aria-label={done ? `Feito: ${label}` : `Pendente: ${label}`}
              >
                {done ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Circle className="h-5 w-5 text-gray-300 dark:text-gray-600 hover:text-brand-400 transition-colors" />
                )}
              </span>

              {/* Texto */}
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-sm font-medium truncate',
                    done
                      ? 'text-gray-400 dark:text-gray-600 line-through'
                      : 'text-gray-900 dark:text-white',
                  )}
                >
                  {label}
                </p>
                {!done && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {description}
                  </p>
                )}
              </div>

              {/* Link de ação */}
              {!done && (
                <Link
                  to={href}
                  className="shrink-0 flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                >
                  Ir
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
